import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { CREW, STATS } from "../lib/site";
import { Seo } from "../components/Seo";

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        path="/about"
        title="About — A Three-Man Import Shop in Hermiston, OR"
        description="Dirty Wrenches Automotive opened in 2022 — a small, owner-operated import specialist shop in Hermiston, Oregon. Meet the crew: Donnie H. (owner / lead tech) and two recently-joined techs covering drivetrain, performance, and Subaru."
      />
      <PageHeader
        code="// 03"
        kicker="About"
        title={<>Three guys.<br />One lift each.</>}
        subtitle="Dirty Wrenches isn't a chain, a franchise or a dealership alternative dressed up in expensive coffee. It's three mechanics who got tired of doing this the wrong way."
        testId="about-header"
      />

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 space-y-6 text-white/75 leading-relaxed text-base md:text-lg max-w-2xl">
            <p>
              Travis opened the doors in 2022 out of his own garage with one bay and one car belonging to a friend who didn't trust the dealer. A few years and several hundred cars later between us all, the formula hasn't changed: imports are our specialty, but we still work on everything else — domestics, trucks, motorcycles — because no job is too small.
            </p>
            <p>
              You'll always speak to the person doing the work. The owner wrenches. The drivetrain specialist is across the floor, not in a different time zone. The performance tuner is the same person who'll torque your wheel nuts.
            </p>
            <p>
              That means we book a couple of weeks out — and we're fine with that. So is everyone who's been here before.
            </p>
            <p>
              Small shop, big bench. Between the three of us, Dirty Wrenches has brought in decades of combined experience, the help and second opinions that come with working shoulder-to-shoulder, and the kind of model-specific knowledge that usually only lives inside a dealer service department. Small doesn't mean light on expertise — it means none of it gets watered down before it reaches your car.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1570129476815-ba368ac77013?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85"
                alt="Workshop tools"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section data-testid="about-stats" className="px-6 md:px-12 lg:px-24 py-16 border-b border-white/10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {STATS.map((s, i) => (
            <div key={s.label} data-testid={`about-stat-${i}`} className="bg-[#0A0A0A] p-8">
              <div className="font-display font-black text-5xl md:text-6xl tracking-tighter text-[#FF3B30]">{s.n}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 border-b border-white/10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="font-mono-tech text-[11px] text-[#FF3B30] tracking-[0.3em]">// 04 — THE CREW</span>
            <h2 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl leading-[0.9]">
              Meet the crew.
            </h2>
            <p className="mt-4 text-white/60 max-w-xl text-sm md:text-base">
              Travis owns the shop and runs day-to-day operations — 20+ years as a lead mechanic across corporate and private shops, with deep import experience. Devon and Jess are recent additions to the crew — both seasoned techs hand-picked to keep the standard up as we grow.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {CREW.map((c, i) => (
            <article
              key={c.name}
              data-testid={`crew-card-${i}`}
              className="bg-[#0A0A0A] hover:bg-[#1A1F24] transition-colors p-8 md:p-10"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="font-mono-tech text-[10px] text-[#FF3B30] tracking-[0.3em] uppercase">{`Crew ${String(i + 1).padStart(2, "0")}`}</div>
                {c.tenure && (
                  <span
                    data-testid={`crew-tenure-${i}`}
                    className={`font-mono-tech text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${
                      c.tenure === "Recently joined"
                        ? "border-[#FF3B30]/50 text-[#FF3B30] bg-[#FF3B30]/10"
                        : "border-white/15 text-white/60"
                    }`}
                  >
                    {c.tenure}
                  </span>
                )}
              </div>
              <h3 className="font-display font-black uppercase tracking-tight text-3xl mt-3">{c.name}</h3>
              <div className="text-white/50 text-sm mt-1">{c.role}</div>
              <div className="mt-5 inline-block bg-[#FF3B30]/15 text-[#FF3B30] font-mono-tech text-[11px] uppercase tracking-wider px-3 py-1">{c.spec}</div>
              <p className="mt-5 text-white/65 leading-relaxed text-sm">{c.bio}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 stripes">
        <div className="bg-[#1A1F24] border border-white/10 p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95]">
              Want to drop a car off?
            </h2>
            <p className="mt-3 text-white/65 max-w-xl">
              Tell us what it is and what it's doing. We'll get you on the schedule.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="about-cta-btn"
            className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5722] text-white text-xs font-bold uppercase tracking-[0.25em] px-7 py-4 transition-colors"
          >
            Get in touch
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
